import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength } from "@/components/PasswordStrength";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Users, Plus, Power, Loader2, KeyRound, ArrowRightLeft, Trash2 } from "lucide-react";

const ROLE_OPTS = {
  SUPER_ADMIN: [["COMPANY_ADMIN", "Company Admin"], ["IT_ADMIN", "IT Admin"], ["SERVICE_HEAD", "Service Head"], ["SUPPORT_TEAM", "Support Team"], ["BRANCH_ADMIN", "Branch Admin"], ["RECEPTIONIST", "Receptionist"]],
  COMPANY_ADMIN: [["IT_ADMIN", "IT Admin"], ["SERVICE_HEAD", "Service Head"], ["SUPPORT_TEAM", "Support Team"], ["BRANCH_ADMIN", "Branch Admin"], ["RECEPTIONIST", "Receptionist"]],
  IT_ADMIN: [["SERVICE_HEAD", "Service Head"], ["SUPPORT_TEAM", "Support Team"], ["BRANCH_ADMIN", "Branch Admin"], ["RECEPTIONIST", "Receptionist"]],
  BRANCH_ADMIN: [["RECEPTIONIST", "Receptionist"]],
};
const DEPARTMENTS = ["IT", "Electrical", "HVAC", "Housekeeping", "Plumbing", "Security", "Furniture", "General"];
const PERM_MODULES = [["dashboard", "Dashboard"], ["visitor_management", "Visitor Management"], ["meeting_rooms", "Meeting Rooms"], ["ticketing", "Ticketing"], ["reports", "Reports"], ["audit_logs", "Audit Logs"], ["users", "Users"], ["settings", "Settings"]];
const PERM_ACTIONS = ["view", "create", "edit", "manage"];

export default function UsersPage() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwForm, setPwForm] = useState({});
  const [pwSaving, setPwSaving] = useState(false);
  const [reU, setReU] = useState(null);
  const [reForm, setReForm] = useState({});
  const [reSaving, setReSaving] = useState(false);
  const [delU, setDelU] = useState(null);

  const ALLOWED = {
    SUPER_ADMIN: ["COMPANY_ADMIN", "IT_ADMIN", "SERVICE_HEAD", "SUPPORT_TEAM", "BRANCH_ADMIN", "RECEPTIONIST"],
    COMPANY_ADMIN: ["IT_ADMIN", "SERVICE_HEAD", "SUPPORT_TEAM", "BRANCH_ADMIN", "RECEPTIONIST"],
    IT_ADMIN: ["SERVICE_HEAD", "SUPPORT_TEAM", "BRANCH_ADMIN", "RECEPTIONIST"],
    BRANCH_ADMIN: ["RECEPTIONIST"],
  };
  const canDelete = (u) => (ALLOWED[user.role] || []).includes(u.role);
  const canReassign = (u) => ["SUPER_ADMIN", "COMPANY_ADMIN"].includes(user.role) && ["BRANCH_ADMIN", "RECEPTIONIST"].includes(u.role);

  const load = () => api.get("/users").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
    if (user.role === "SUPER_ADMIN") api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setForm({ role: ROLE_OPTS[user.role]?.[0]?.[0] }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try { await api.post("/users", form); toast.success("User created"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const toggle = async (u) => { try { await api.patch(`/users/${u.id}/status`); load(); } catch (e) { toast.error(apiError(e)); } };

  const openReset = (u) => { setPwUser(u); setPwForm({}); };
  const resetPassword = async () => {
    if ((pwForm.new_password || "").length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pwForm.new_password !== pwForm.confirm) { toast.error("Passwords do not match"); return; }
    setPwSaving(true);
    try { await api.patch(`/users/${pwUser.id}/password`, { new_password: pwForm.new_password }); toast.success("Password reset"); setPwUser(null); }
    catch (e) { toast.error(apiError(e)); } finally { setPwSaving(false); }
  };

  const openReassign = (u) => { setReU(u); setReForm({ company_id: u.company_id, branch_id: "" }); };
  const reassign = async () => {
    if (!reForm.branch_id) { toast.error("Please select a branch"); return; }
    setReSaving(true);
    try { await api.patch(`/users/${reU.id}/branch`, { company_id: reForm.company_id, branch_id: reForm.branch_id }); toast.success("User reassigned"); setReU(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setReSaving(false); }
  };
  const doDelete = async () => {
    try { await api.delete(`/users/${delU.id}`); toast.success("User deleted"); setDelU(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const branchOpts = user.role === "SUPER_ADMIN" && form.company_id ? branches.filter((b) => b.company_id === form.company_id) : branches;
  const needsBranch = ["BRANCH_ADMIN", "RECEPTIONIST", "SUPPORT_TEAM"].includes(form.role);

  if (!list) return <Loader />;

  return (
    <div>
      <PageHeader title="Users" subtitle="Admins and receptionists across your organization."
        actions={<Button onClick={openNew} data-testid="add-user-btn" className="bg-navy-900 hover:bg-navy-800"><Plus size={16} className="mr-1.5" /> New User</Button>} />

      {list.length === 0 ? <EmptyState icon={Users} title="No users yet" /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden sf-fade-up">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead>
              <TableHead>Company</TableHead><TableHead>Branch</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium text-navy-900">{u.name}</TableCell>
                  <TableCell className="text-slate-500">{u.email}</TableCell>
                  <TableCell><span className="text-xs font-semibold text-navy-800 bg-navy-50 px-2 py-1 rounded-md">{u.role.replace(/_/g, " ")}</span></TableCell>
                  <TableCell className="text-slate-500">{u.company_name}</TableCell>
                  <TableCell className="text-slate-500">{u.branch_name}</TableCell>
                  <TableCell><StatusBadge status={u.active ? "active" : "inactive"} /></TableCell>
                  <TableCell className="text-right space-x-2 whitespace-nowrap">
                    {canReassign(u) && <Button size="sm" variant="outline" onClick={() => openReassign(u)} data-testid={`reassign-user-${u.email}`} title="Reassign to another branch"><ArrowRightLeft size={14} /></Button>}
                    <Button size="sm" variant="outline" onClick={() => openReset(u)} data-testid={`reset-pw-${u.email}`} title="Reset password"><KeyRound size={14} /></Button>
                    <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`toggle-user-${u.email}`} title="Toggle active"><Power size={14} /></Button>
                    {canDelete(u) && <Button size="sm" variant="outline" onClick={() => setDelU(u)} data-testid={`delete-user-${u.email}`} title="Delete user" className="border-rose-200 text-rose-600 hover:bg-rose-50"><Trash2 size={14} /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input data-testid="user-name" className="mt-1 h-10" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email *</Label><Input type="email" data-testid="user-email" className="mt-1 h-10" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Password *</Label><PasswordInput testId="user-password" className="mt-1 h-10" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} /><PasswordStrength value={form.password} testId="user-password-strength" /></div>
            <div><Label>Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{(ROLE_OPTS[user.role] || []).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {user.role === "SUPER_ADMIN" && (
              <div><Label>Company *</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v, branch_id: undefined })}>
                  <SelectTrigger className="mt-1" data-testid="user-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {needsBranch && user.role !== "BRANCH_ADMIN" && (
              <div><Label>Branch *</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branchOpts.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.role === "SUPPORT_TEAM" && (
              <div><Label>Department *</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger className="mt-1" data-testid="user-department"><SelectValue placeholder="Select department" /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {form.role === "SERVICE_HEAD" && (
              <div className="space-y-2">
                <Label>Service Head Scope *</Label>
                <div className="flex gap-4 text-sm text-navy-900">
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="scope" data-testid="scope-company" checked={form.scope_type !== "branches"} onChange={() => setForm({ ...form, scope_type: "company", branch_ids: [] })} /> Entire Company</label>
                  <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="scope" data-testid="scope-branches" checked={form.scope_type === "branches"} onChange={() => setForm({ ...form, scope_type: "branches" })} /> Specific Branches</label>
                </div>
                {form.scope_type === "branches" && (
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
                    {branchOpts.map((b) => (
                      <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={(form.branch_ids || []).includes(b.id)} onChange={(e) => { const s = new Set(form.branch_ids || []); e.target.checked ? s.add(b.id) : s.delete(b.id); setForm({ ...form, branch_ids: [...s] }); }} /> {b.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            {form.role === "IT_ADMIN" && (
              <div className="space-y-2">
                <Label>Access Control (module permissions)</Label>
                <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
                  <div className="grid grid-cols-5 bg-slate-50 font-semibold px-2 py-1.5"><span>Module</span>{PERM_ACTIONS.map((a) => <span key={a} className="text-center capitalize">{a}</span>)}</div>
                  {PERM_MODULES.map(([m, l]) => (
                    <div key={m} className="grid grid-cols-5 items-center px-2 py-1.5 border-t border-slate-100">
                      <span className="text-navy-900">{l}</span>
                      {PERM_ACTIONS.map((a) => (
                        <span key={a} className="text-center"><input type="checkbox" data-testid={`perm-${m}-${a}`} checked={!!(form.permissions?.[m]?.[a])} onChange={(e) => { const p = { ...(form.permissions || {}) }; p[m] = { ...(p[m] || {}), [a]: e.target.checked }; setForm({ ...form, permissions: p }); }} /></span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {["SERVICE_HEAD", "SUPPORT_TEAM", "IT_ADMIN"].includes(form.role) && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Employee ID</Label><Input data-testid="user-empid" className="mt-1 h-10" value={form.employee_id || ""} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} /></div>
                <div><Label>Mobile</Label><Input data-testid="user-phone" className="mt-1 h-10" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-user-btn" className="bg-navy-900">{saving ? <Loader2 className="animate-spin" size={16} /> : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwUser} onOpenChange={(o) => !o && setPwUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password{pwUser ? ` · ${pwUser.name}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Set a new password for <span className="font-medium text-navy-900">{pwUser?.email}</span>. They'll use it the next time they sign in.</p>
            <div><Label>New Password *</Label><PasswordInput testId="reset-password-input" className="mt-1 h-10" value={pwForm.new_password || ""} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} /><PasswordStrength value={pwForm.new_password} testId="reset-password-strength" /></div>
            <div><Label>Confirm Password *</Label><PasswordInput testId="reset-password-confirm" className="mt-1 h-10" value={pwForm.confirm || ""} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwUser(null)}>Cancel</Button>
            <Button onClick={resetPassword} disabled={pwSaving} data-testid="save-reset-password-btn" className="bg-navy-900">{pwSaving ? <Loader2 className="animate-spin" size={16} /> : "Reset Password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reU} onOpenChange={(o) => !o && setReU(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reassign{reU ? ` · ${reU.name}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Move <span className="font-medium text-navy-900">{reU?.email}</span> to a different branch.</p>
            {user.role === "SUPER_ADMIN" && (
              <div><Label>Company</Label>
                <Select value={reForm.company_id} onValueChange={(v) => setReForm({ ...reForm, company_id: v, branch_id: "" })}>
                  <SelectTrigger className="mt-1" data-testid="reassign-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Branch *</Label>
              <Select value={reForm.branch_id} onValueChange={(v) => setReForm({ ...reForm, branch_id: v })}>
                <SelectTrigger className="mt-1" data-testid="reassign-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
                <SelectContent>{branches.filter((b) => user.role !== "SUPER_ADMIN" || b.company_id === reForm.company_id).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReU(null)}>Cancel</Button>
            <Button onClick={reassign} disabled={reSaving} data-testid="save-reassign-btn" className="bg-navy-900">{reSaving ? <Loader2 className="animate-spin" size={16} /> : "Reassign"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delU} onOpenChange={(o) => !o && setDelU(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete User</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Permanently delete <span className="font-medium text-navy-900">{delU?.name}</span> ({delU?.email})? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelU(null)}>Cancel</Button>
            <Button onClick={doDelete} data-testid="confirm-delete-user-btn" className="bg-rose-600 hover:bg-rose-700">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
